import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, ProductBadge } from "../components/Badges";
import { WORKFLOWS, STATUS_STAGES, PRODUCT_TYPES } from "../lib/workflows";
import ChatPanel from "../components/ChatPanel";
import { DatePicker } from "../components/DatePicker";
import { SearchNoteModal } from "../components/SearchNoteModal";
import { isRushText } from "../components/RequirementsProgress";
import { TurnaroundTimer } from "../components/RequirementsProgress";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle,
  AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "../components/ui/alert-dialog";
import JitsiRoom from "../components/JitsiRoom";
import { fileUrl, humanSize } from "../lib/apiExtra";
import { slaInfo, historyWithDurations, humanizeDuration, abstractorSla, isAbstractorOrder } from "../lib/sla";
import { stateName } from "../lib/states";
import { toast } from "sonner";
import { ArrowLeft, Lock, MapPin, Hash, Save, Clock, CheckCircle2, User, ExternalLink,
  Link2, FileText, UploadCloud, Sparkles, Loader2, Download, ClipboardList, Video, MessageSquare, Mail, Copy, X, AlertTriangle, Plus, Search, RefreshCw } from "lucide-react";

const STATUSES = STATUS_STAGES;

export default function OrderWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const deedInput = useRef(null);
  const [order, setOrder] = useState(null);
  const [ws, setWs] = useState({});
  const [report, setReport] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [autofilling, setAutofilling] = useState(false);
  const [instrConfig, setInstrConfig] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showCall, setShowCall] = useState(false);
  const [attorney, setAttorney] = useState(null);
  const [templates, setTemplates] = useState({});
  const [reportType, setReportType] = useState("Typing");
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [genSummary, setGenSummary] = useState(false);
  const [searchNoteOpen, setSearchNoteOpen] = useState(false);
  const docInput = useRef(null);
  const caInput = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      setWs(data.workspace_data || {});
      setReport(data.report_data || {});
      setReportType(data.report_data?.report_type || "Typing");
    } catch (e) {
      if (e?.response?.status === 403 && e?.response?.data?.detail === "clock_in_required") {
        toast.error("Clock in on the Time Clock to open your orders");
        navigate("/dashboard");
      } else { toast.error("Order not found"); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 60000); return () => clearInterval(t); }, []);
  const [snData, setSnData] = useState(null);
  useEffect(() => { api.get(`/orders/${id}/search-note/data`).then((r) => setSnData(r.data.search_note)).catch(() => {}); }, [id]);
  const [research, setResearch] = useState(null);
  const [researchLoading, setResearchLoading] = useState(false);
  const [copiedResearch, setCopiedResearch] = useState(false);
  const fetchResearch = async (refresh = false) => {
    setResearchLoading(true);
    try { const { data } = await api.get(`/orders/${id}/research${refresh ? "?refresh=true" : ""}`); setResearch(data); }
    catch { if (refresh) toast.error("Could not refresh research links"); }
    finally { setResearchLoading(false); }
  };
  useEffect(() => { if (id) fetchResearch(false); /* eslint-disable-next-line */ }, [id]);
  const researchCategories = (research && research.categories) || [];
  const researchItems = researchCategories.flatMap((c) => (c.links || []).map((l) => {
    if (l.url) return l;
    const q = encodeURIComponent(`${order?.county || ""} County ${stateName(order?.state)} ${l.label.split(" (")[0]} official website`);
    return { ...l, url: `https://www.google.com/search?q=${q}`, fallback: true };
  }));
  const confirmResearch = async () => {
    try { await api.post(`/orders/${id}/research/confirm`); setResearch((r) => ({ ...(r || {}), verified: true })); toast.success("County links confirmed for the whole team"); }
    catch { toast.error("Could not confirm"); }
  };
  const copyAllResearch = async () => {
    const text = researchItems.map((l) => `${l.label}: ${l.url}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopiedResearch(true);
      setTimeout(() => setCopiedResearch(false), 2000);
      toast.success(`Copied ${researchItems.length} research links`);
    } catch { toast.error("Copy failed — select and copy manually"); }
  };
  useEffect(() => { if (order) setNotesDraft(order.notes || ""); }, [order?.id]);
  useEffect(() => { api.get("/config/instructions").then((r) => setInstrConfig(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/report/templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/orders/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrder(data);
      toast.success("Document uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploadingDoc(false); if (docInput.current) docInput.current.value = ""; }
  };

  const changeInstructionSet = async (v) => {
    try { const { data } = await api.patch(`/orders/${id}/instruction-set`, { instruction_set: v }); setOrder(data); toast.success("Instruction set updated"); }
    catch { toast.error("Could not update instruction set"); }
  };

  const downloadReportPdf = async () => {
    try { const { data } = await api.post(`/orders/${id}/report-link?report_type=${encodeURIComponent(reportType)}`); window.open(`${process.env.REACT_APP_BACKEND_URL}${data.url}`, "_blank"); }
    catch { toast.error("Could not generate report link"); }
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewTab, setPreviewTab] = useState("pdf");
  const openPreview = async () => {
    setPreviewLoading(true); setPreviewOpen(true); setPreviewPdfUrl(null); setPreviewTab("pdf");
    try {
      const { data } = await api.get(`/orders/${id}/report/preview?report_type=${encodeURIComponent(reportType)}`);
      setPreviewData(data);
      try {
        const res = await api.get(`/orders/${id}/report-rendered.pdf?report_type=${encodeURIComponent(reportType)}`, { responseType: "blob" });
        setPreviewPdfUrl(URL.createObjectURL(res.data));
      } catch { setPreviewTab("details"); }
    }
    catch { toast.error("Could not load preview"); setPreviewOpen(false); }
    finally { setPreviewLoading(false); }
  };

  const downloadSearchNote = async () => {
    try {
      const { data } = await api.get(`/orders/${id}/search-note`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `SearchNote-${(order?.order_ref || "order").replace(/\//g, "-")}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download search note"); }
  };

  const [generatingDelv, setGeneratingDelv] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const generateDeliverables = async () => {
    setGeneratingDelv(true);
    try {
      const { data } = await api.post(`/orders/${id}/deliverables/generate`);
      setOrder(data.order);
      const c = data.chain || {};
      toast.success(`Deliverables generated · Chain: ${c.open || 0} open, ${c.released || 0} released mortgage(s)`);
    } catch { toast.error("Could not generate deliverables"); }
    finally { setGeneratingDelv(false); }
  };
  const polishChain = async () => {
    setPolishing(true);
    try {
      const { data } = await api.post(`/orders/${id}/chain/polish`);
      setOrder(data.order); setReport(data.order.report_data || {});
      const c = data.chain || {};
      toast.success(`Title chain polished · ${c.open || 0} open, ${c.released || 0} released, ${c.satisfactions || 0} satisfaction(s)`);
    } catch { toast.error("Could not polish title chain"); }
    finally { setPolishing(false); }
  };
  const downloadDeliverable = async (kind) => {
    const path = { name_index: "name-index.pdf", search_note: "search-note.pdf", search_package: "search-package.pdf", assignment: "assignment-package.pdf" }[kind] || "search-note.pdf";
    const label = { name_index: "NameSearchIndex", search_note: "SearchNote", search_package: "DuvalSearchPackage", assignment: "Assignment" }[kind] || "Deliverable";
    try {
      const res = await api.get(`/orders/${id}/${path}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${label}-${(order?.order_ref || "order").replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };
  const downloadPacket = async () => {
    try {
      const res = await api.get(`/orders/${id}/handoff-packet.zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `HandoffPacket-${(order?.order_ref || "order").replace(/\//g, "-")}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Hand-off packet downloaded");
    } catch { toast.error("Could not download packet"); }
  };
  const [emailingPacket, setEmailingPacket] = useState(false);
  const emailPacket = async () => {
    if (!window.confirm("Email a secure download link for the hand-off packet to the client on file?")) return;
    setEmailingPacket(true);
    try {
      const { data } = await api.post(`/orders/${id}/email-packet`);
      setOrder((o) => ({ ...(o || {}), packet_email: data.packet_email }));
      toast.success(`Packet link emailed to ${data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not email packet"); }
    finally { setEmailingPacket(false); }
  };

  const [ds, setDs] = useState(null);
  const dsPoll = useRef(null);
  const [dsStarting, setDsStarting] = useState(false);
  const pollDs = async () => {
    try {
      const { data } = await api.get(`/orders/${id}/digital-search/status`);
      setDs(data.digital_search);
      if (data.digital_search && data.digital_search.status === "running") {
        dsPoll.current = setTimeout(pollDs, 4000);
      } else { load(); }
    } catch { /* keep last state */ }
  };
  const runDigitalSearch = async () => {
    setDsStarting(true);
    try {
      const { data } = await api.post(`/orders/${id}/digital-search/run`);
      setDs(data.digital_search);
      toast.success("Digital search started — capturing county portals…");
      clearTimeout(dsPoll.current); dsPoll.current = setTimeout(pollDs, 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start digital search"); }
    finally { setDsStarting(false); }
  };
  const retryBlocked = async () => {
    setDsStarting(true);
    try {
      const { data } = await api.post(`/orders/${id}/digital-search/retry-blocked`);
      setDs(data.digital_search);
      toast.success(`Retrying ${data.retrying.length} portal(s)…`);
      clearTimeout(dsPoll.current); dsPoll.current = setTimeout(pollDs, 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Nothing to retry"); }
    finally { setDsStarting(false); }
  };
  const changeProductType = async (pt) => {
    try {
      const { data } = await api.patch(`/orders/${id}/product-type`, { product_type: pt });
      setOrder(data);
      toast.success(`Product type set to ${pt}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change product type"); }
  };
  const [abstractorBusy, setAbstractorBusy] = useState(false);
  const abstractorAccept = async () => {
    if (!window.confirm("Accept ProTitleUSA's quote and email them to proceed?")) return;
    setAbstractorBusy(true);
    try {
      const { data } = await api.post(`/orders/${id}/abstractor/accept`);
      setOrder(data);
      toast.success("Quote accepted — ProTitleUSA notified to proceed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept quote"); }
    finally { setAbstractorBusy(false); }
  };
  const abstractorClarify = async () => {
    const message = window.prompt("What should we ask ProTitleUSA? (this is emailed to them)");
    if (!message || !message.trim()) return;
    setAbstractorBusy(true);
    try {
      const { data } = await api.post(`/orders/${id}/abstractor/clarify`, { message: message.trim() });
      setOrder(data);
      toast.success("Follow-up emailed to ProTitleUSA");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send follow-up"); }
    finally { setAbstractorBusy(false); }
  };
  useEffect(() => () => clearTimeout(dsPoll.current), []);
  useEffect(() => {
    if (order?.digital_search) {
      setDs(order.digital_search);
      if (order.digital_search.status === "running" && !dsPoll.current) {
        dsPoll.current = setTimeout(pollDs, 4000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.digital_search]);

  const openAttorney = async () => {
    try { const { data } = await api.get(`/orders/${id}/attorney-certificate`); setAttorney(data); }
    catch { toast.error("Could not generate certificate email"); }
  };
  const copyAttorney = async () => {
    try { await navigator.clipboard.writeText(`Subject: ${attorney.subject}\n\n${attorney.body}`); toast.success("Email copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };

  const uploadClientDoc = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/orders/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrder(data);
      toast.success("Client document attached");
    } catch { toast.error("Upload failed"); }
    finally { if (caInput.current) caInput.current.value = ""; }
  };

  if (!order) return <div className="h-40 rounded-md bg-slate-100 animate-pulse" />;

  const wf = WORKFLOWS[order.product_type] || WORKFLOWS["Full Search"];

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/orders/${id}/status`, { status, note });
      setOrder(data);
      setNote("");
      toast.success(`Status → ${status}`);
    } catch { toast.error("Status update failed"); }
  };

  const saveWorkspace = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/orders/${id}/workspace`, { workspace_data: ws });
      setOrder(data);
      toast.success("Workspace saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const saveReport = async () => {
    setSavingReport(true);
    try {
      const { data } = await api.patch(`/orders/${id}/report`, { report_data: { ...report, report_type: reportType } });
      setOrder(data);
      toast.success("Report saved");
    } catch { toast.error("Report save failed"); }
    finally { setSavingReport(false); }
  };

  const downloadReportDocx = async () => {
    try {
      const { data } = await api.get(`/orders/${id}/report.docx?report_type=${encodeURIComponent(reportType)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `Report-${(order?.order_ref || "order").replace(/\//g, "-")}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download Word report"); }
  };

  const downloadBlankTemplate = async (rtype) => {
    try {
      const { data } = await api.get(`/orders/${id}/blank-template/${rtype}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const company = (order?.company || "").toLowerCase().includes("titlepriority") ? "TitlePriority" : "YDeal";
      const kind = rtype === "lnv" ? "LNV" : "Typing";
      const a = document.createElement("a");
      a.href = url; a.download = `${company}-${kind}-Template-${(order?.order_ref || "order").replace(/\//g, "-")}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const finalizeReport = async () => {
    setConfirmFinalize(false);
    const unv = [...aiFilled].filter((k) => !verifiedSet.has(k)).length;
    if (unv > 0 && !window.confirm(`${unv} AI-filled field(s) are not yet marked verified. Finalize and lock the report anyway?`)) return;
    try {
      const { data } = await api.post(`/orders/${id}/report/finalize`);
      setOrder(data);
      const parts = [];
      if (data.final_report) parts.push("Report PDF");
      const kinds = { name_index: "Name Index", search_note: "Search Note", search_package: "Search Package" };
      (data.generated_docs || []).forEach((g) => { if (kinds[g.kind]) parts.push(kinds[g.kind]); });
      toast.success(parts.length
        ? `Report finalized & locked — hand-off packet ready: ${parts.join(", ")}`
        : "Report finalized & locked");
    }
    catch { toast.error("Could not finalize"); }
  };

  const markReviewed = async () => {
    try { const { data } = await api.post(`/orders/${id}/mark-reviewed`); setOrder(data); toast.success("Marked as reviewed"); }
    catch { toast.error("Could not mark reviewed"); }
  };

  const markSearchComplete = async () => {
    if (!window.confirm("Mark the search complete and move this order to Typing?")) return;
    try { const { data } = await api.post(`/orders/${id}/search-complete`); setOrder(data); toast.success("Search complete → InProgress for Typing"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not complete search"); }
  };

  const markTypingComplete = async () => {
    if (!window.confirm("Mark typing complete and move this order to Final QC?")) return;
    try { const { data } = await api.post(`/orders/${id}/typing-complete`); setOrder(data); toast.success("Typing complete → InProgress for Final QC"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not complete typing"); }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try { const { data } = await api.patch(`/orders/${id}/notes`, { notes: notesDraft }); setOrder(data); toast.success("Notes saved"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not save notes"); }
    finally { setSavingNotes(false); }
  };

  const submitToClient = async () => {
    if (!window.confirm("Submit this order to the client? It will be marked Submitted and delivery timestamped.")) return;
    try { const { data } = await api.post(`/orders/${id}/submit`); setOrder(data); toast.success("Submitted to client"); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not submit order"); }
  };

  const toggleRequirement = async (i) => {
    const done = [...(order.requirements_done || [])];
    while (done.length < (order.requirements || []).length) done.push(false);
    done[i] = !done[i];
    try { const { data } = await api.put(`/orders/${id}/requirements`, { done }); setOrder(data); }
    catch { toast.error("Could not update requirement"); }
  };

  const generateSummary = async () => {
    setGenSummary(true);
    try { const { data } = await api.post(`/orders/${id}/ai-summary`); setOrder(data); toast.success("AI summary generated"); }
    catch { toast.error("Could not generate summary"); }
    setGenSummary(false);
  };  const reopenReport = async () => {
    try { const { data } = await api.post(`/orders/${id}/report/reopen`); setOrder(data); toast.success("Report reopened for editing"); }
    catch { toast.error("Could not reopen"); }
  };

  const extractDeed = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/orders/${id}/extract-deed`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrder(data.order);
      setReport(data.order.report_data || {});
      toast.success("Legal Description & Vesting pre-filled by AI");
    } catch { toast.error("Deed extraction failed"); }
    finally { setExtracting(false); }
  };

  const pollAutofill = async (attempt = 0) => {
    try {
      const { data } = await api.get(`/orders/${id}/report/autofill-status`);
      const st = data.job?.status;
      if (st === "processing" && attempt < 120) { setTimeout(() => pollAutofill(attempt + 1), 3000); return; }
      if (st === "done") {
        setOrder(data.order);
        setReport(data.order.report_data || {});
        const n = data.job?.filled || 0;
        toast.success(n > 0 ? `AI filled ${n} report field(s) from the package — review below.` : "Package read, but no new fields could be extracted.", { duration: 7000 });
        setAutofilling(false);
      } else if (st === "error") { toast.error("Auto-fill hit an error — check the package and try again."); setAutofilling(false); }
      else if (attempt >= 120) { toast.error("Auto-fill is taking too long — please try again."); setAutofilling(false); }
    } catch { setAutofilling(false); }
  };

  const autofillReport = async (file) => {
    if (!file) return;
    setAutofilling(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/orders/${id}/report/autofill?report_type=${encodeURIComponent(reportType)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message || "Reading the package…");
      setTimeout(() => pollAutofill(0), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start auto-fill");
      setAutofilling(false);
    }
  };

  const autofillFromDocs = async () => {
    setAutofilling(true);
    try {
      const { data } = await api.post(`/orders/${id}/report/autofill-from-docs?report_type=${encodeURIComponent(reportType)}`);
      toast.success(data.message || "Reading the attached package…");
      setTimeout(() => pollAutofill(0), 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No attached package to read");
      setAutofilling(false);
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const location = [order.county && `${order.county} County`, stateName(order.state), order.zip_code].filter(Boolean).join(", ");
  const locked = order?.report_status === "Final";
  const aiFilled = new Set(order?.report_data?.autofill_job?.fields || []);
  const verifiedSet = new Set(report.verified_fields || []);
  const aiTinted = (k) => aiFilled.has(k) && !locked && !verifiedSet.has(k);
  const toggleVerified = (k) => {
    const cur = new Set(report.verified_fields || []);
    cur.has(k) ? cur.delete(k) : cur.add(k);
    setReport({ ...report, verified_fields: [...cur] });
  };
  const setArray = (key, arr) => setReport({ ...report, [key]: arr });

  return (
    <div className="ws-light space-y-6 -m-6 p-6 bg-slate-100 min-h-[calc(100vh-3.5rem)] text-slate-800">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1A395E] transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-700 tracking-tight text-slate-800 flex items-center gap-2">
                <Hash className="h-5 w-5 text-slate-700" />
                <span className="font-mono" data-testid="order-ref">{order.order_ref}</span>
              </h1>
              <ProductBadge product={order.product_type} testid="order-product" />
              <StatusBadge status={order.status} testid="order-status" />
              <span data-testid="order-stage" className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${order.stage === "Typing" ? "border-amber-400 bg-amber-100 text-amber-800" : "border-blue-300 bg-blue-100 text-blue-800"}`}>
                {order.stage === "Typing" ? "Typing / Commitment" : "Search"} stage
              </span>
              {order.merged_count > 0 && (
                <span data-testid="merged-tag" title="Duplicate emails for this order were merged"
                  className="inline-flex items-center gap-1 rounded-full border border-teal/40 bg-accent px-2.5 py-0.5 text-[10px] font-bold text-accent-foreground">
                  <Mail className="h-3 w-3" /> merged from {order.merged_count + 1} emails
                </span>
              )}
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-700">
              <MapPin className="h-4 w-4 text-slate-500" />
              <span data-testid="order-address">{order.property_address}</span>
            </div>
            {location && <div className="mt-1 text-xs text-slate-500 font-mono" data-testid="order-location">{location}</div>}
            <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
              <User className="h-3.5 w-3.5" /> {order.assigned_to_name || "Unassigned"} · Source: {order.source}
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs">
              {order.borrower && <div data-testid="order-borrower"><span className="text-slate-500">Borrower:</span> <span className="text-slate-700 font-medium">{order.borrower}</span></div>}
              {order.parcel_id && <div data-testid="order-parcel"><span className="text-slate-500">Parcel ID:</span> <span className="text-slate-700 font-medium">{order.parcel_id}</span></div>}
              {order.customer_file_id && <div data-testid="order-customer-file"><span className="text-slate-500">Customer File:</span> <span className="text-slate-700 font-medium">{order.customer_file_id}</span></div>}
              {order.company && <div data-testid="order-company"><span className="text-slate-500">Company:</span> <span className="text-slate-700 font-medium">{order.company}</span></div>}
            </div>
            {isAdmin && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-slate-500">Change Product Type:</span>
                <select data-testid="product-type-select" value={order.product_type}
                  onChange={(e) => changeProductType(e.target.value)}
                  className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700">
                  {PRODUCT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}
            {order.assignment_package && (
              <div data-testid="assignment-package-note" className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-fuchsia-300 bg-fuchsia-50 px-2.5 py-1 text-[11px] font-semibold text-fuchsia-800">
                  <Mail className="h-3.5 w-3.5" /> Abstractor package generated ({order.assignment_package.product}) · emailed to ProTitleUSA
                </span>
              </div>
            )}
            {order.abstractor_reply && (() => {
              const asla = abstractorSla(order);
              const dec = order.abstractor_reply.decision;
              const decCfg = { accepted: ["Accepted", "bg-emerald-100 text-emerald-800 border-emerald-300"],
                declined: ["Declined", "bg-rose-100 text-rose-800 border-rose-300"],
                needs_clarification: ["Needs clarification", "bg-amber-100 text-amber-800 border-amber-300"] }[dec];
              const lvl = { red: "bg-rose-100 text-rose-800 border-rose-300", amber: "bg-amber-100 text-amber-800 border-amber-300",
                green: "bg-emerald-100 text-emerald-800 border-emerald-300", slate: "bg-slate-100 text-slate-700 border-slate-300" };
              return (
                <div data-testid="abstractor-reply" className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-900">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold">
                    <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> ProTitleUSA replied</span>
                    {decCfg && <span data-testid="abstractor-reply-decision" className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${decCfg[1]}`}>{decCfg[0]}</span>}
                    {order.abstractor_reply.eta && <span data-testid="abstractor-reply-eta">ETA: {order.abstractor_reply.eta}</span>}
                    {order.abstractor_reply.price && <span data-testid="abstractor-reply-price">Price: {order.abstractor_reply.price}</span>}
                    {asla?.kind === "due" && <span data-testid="abstractor-sla" className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${lvl[asla.level]}`}><Clock className="h-3 w-3" /> {asla.label}</span>}
                    {order.abstractor_reply.received_at && <span className="font-normal text-emerald-700">· {fmt(order.abstractor_reply.received_at)}</span>}
                  </div>
                  {order.abstractor_reply.notes && (
                    <div data-testid="abstractor-reply-notes" className="mt-1 font-normal text-emerald-800/90"><b>Note:</b> {order.abstractor_reply.notes}</div>
                  )}
                  {order.abstractor_reply.snippet && (
                    <div className="mt-1 max-h-16 overflow-hidden whitespace-pre-line font-normal text-emerald-800/90">{order.abstractor_reply.snippet}</div>
                  )}
                  {isAdmin && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button data-testid="abstractor-accept-btn" onClick={abstractorAccept} disabled={abstractorBusy}
                        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Accept quote
                      </button>
                      <button data-testid="abstractor-clarify-btn" onClick={abstractorClarify} disabled={abstractorBusy}
                        className="inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-50 transition-colors disabled:opacity-50">
                        <Mail className="h-3.5 w-3.5" /> Ask again
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
            {!order.abstractor_reply && isAbstractorOrder(order) && (() => {
              const asla = abstractorSla(order);
              if (!asla || asla.kind !== "awaiting") return null;
              return (
                <div data-testid="abstractor-awaiting" className={`mt-2 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-semibold ${asla.stale ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-300 bg-slate-50 text-slate-600"}`}>
                  <Clock className="h-3.5 w-3.5" /> {asla.label}
                </div>
              );
            })()}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {order.stage !== "Typing" && !["Completed", "Cancelled", "Submitted", "InProgress for Final QC", "QC Completed"].includes(order.status) &&
              (isAdmin || String(order.assigned_to) === String(user?.id)) && (
              <button data-testid="mark-search-complete" onClick={markSearchComplete}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors">
                <CheckCircle2 className="h-3.5 w-3.5" /> Mark Search Complete → Typing
              </button>
            )}
            {order.status === "InProgress for Typing" &&
              (isAdmin || String(order.assigned_to) === String(user?.id)) && (
              <button data-testid="mark-typing-complete" onClick={markTypingComplete}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors">
                <CheckCircle2 className="h-3.5 w-3.5" /> Mark Typing Complete → Final QC
              </button>
            )}
            {!["Submitted", "Cancelled"].includes(order.status) && isAdmin && (
              <button data-testid="submit-to-client" onClick={submitToClient}
                className="inline-flex items-center gap-1.5 rounded-md bg-teal px-3 py-2 text-xs font-semibold text-white hover:opacity-90 transition-opacity">
                <CheckCircle2 className="h-3.5 w-3.5" /> Submit to Client
              </button>
            )}
            <button data-testid="download-search-note" onClick={downloadSearchNote}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
              <Download className="h-3.5 w-3.5" /> Download Search Note
            </button>
            {["WV", "NC"].includes((order.state || "").toUpperCase()) && (
              <button data-testid="attorney-cert-btn" onClick={openAttorney}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-2 text-xs font-semibold text-amber-300 hover:bg-amber-500/20 transition-colors">
                <Mail className="h-3.5 w-3.5" /> Attorney Certificate Email
              </button>
            )}
          </div>
        </div>
      </div>

      {attorney && (
        <div data-testid="attorney-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAttorney(null)}>
          <div className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-600 flex items-center gap-2"><Mail className="h-4 w-4 text-amber-600" /> Attorney Opinion Letter Request</h3>
              <button data-testid="attorney-close" onClick={() => setAttorney(null)} className="text-slate-500 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="text-xs text-slate-500 mb-1">Subject</div>
            <input readOnly value={attorney.subject} data-testid="attorney-subject" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm mb-3 bg-slate-50" />
            <div className="text-xs text-slate-500 mb-1">Email Body</div>
            <textarea readOnly value={attorney.body} rows={12} data-testid="attorney-body" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono bg-slate-50" />
            <div className="mt-3 flex items-center gap-2">
              <button data-testid="attorney-copy" onClick={copyAttorney} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-navy-2 transition-colors"><Copy className="h-3.5 w-3.5" /> Copy Email</button>
              <a data-testid="attorney-mailto" href={`mailto:?subject=${encodeURIComponent(attorney.subject)}&body=${encodeURIComponent(attorney.body)}`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"><Mail className="h-3.5 w-3.5" /> Open in Email</a>
            </div>
          </div>
        </div>
      )}

      {(() => {
        const sla = slaInfo(order);
        return sla.breached ? (
          <div data-testid="sla-banner" className="rounded-lg border border-rose-300 bg-rose-50 p-3 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0" />
            <div className="text-sm text-rose-800">
              <span className="font-bold">SLA breached</span> — this order has been in
              <span className="font-semibold"> "{order.status}" </span>
              for {Math.floor(sla.hours)}h (limit {sla.threshold}h). Please action it now.
            </div>
          </div>
        ) : null;
      })()}

      {order.needs_review && (
        <div data-testid="review-banner" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-amber-300">New client reply — needs review</div>
                {order.last_client_reply?.content ? (
                  <div className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md border border-amber-500/20 bg-black/20 p-2 text-sm text-slate-700" data-testid="review-reply-text">
                    {order.last_client_reply.content}
                  </div>
                ) : (
                  <div className="mt-1 text-sm text-slate-700">A client sent a new email on this order.</div>
                )}
                {order.last_client_reply?.via && (
                  <div className="mt-1 text-xs text-amber-400">via {order.last_client_reply.via}{order.last_client_reply.at ? ` · ${fmt(order.last_client_reply.at)}` : ""}</div>
                )}
              </div>
            </div>
            <button data-testid="mark-reviewed-btn" onClick={markReviewed}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700 transition-colors">
              Mark reviewed
            </button>
          </div>
        </div>
      )}

      {/* Blueprint two-column: LEFT = Order Info + AI Summary · RIGHT = Search Note + Typing Report */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-6 space-y-6">
          {/* Order Info */}
          <div data-testid="order-info-card" className="rounded-lg border border-slate-200 bg-white p-5">
            <h3 className="flex items-center gap-2 text-sm font-700 text-slate-800 mb-3"><ClipboardList className="h-4 w-4 text-teal" /> Order Info</h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {[["Order #", order.order_ref], ["Product", order.product_type], ["Status", order.status],
                ["Company", order.company], ["Borrower", order.borrower], ["Parcel/APN", order.parcel_id],
                ["Property", order.property_address], ["County", order.county && `${order.county} County`],
                ["State", stateName(order.state)], ["ZIP", order.zip_code], ["Assignee", order.assigned_to_name],
                ["Created", order.created_at ? fmt(order.created_at) : ""]].map(([k, v]) => (v ? (
                <div key={k} className={k === "Property" ? "col-span-2" : ""}>
                  <dt className="text-[11px] uppercase tracking-wide text-slate-500">{k}</dt>
                  <dd className="text-slate-700">{v}</dd>
                </div>
              ) : null))}
            </dl>
          </div>

          {/* Client Requirements & Instructions (from Order Summary/Details) */}
          {(order.requirements?.length > 0 || order.instructions) && (
            <div data-testid="requirements-card" className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="flex items-center gap-2 text-sm font-700 text-slate-800"><ClipboardList className="h-4 w-4 text-teal" /> Client Requirements &amp; Instructions</h3>
                <div className="flex items-center gap-2">
                  <TurnaroundTimer order={order} testid="turnaround-timer" />
                  {order.requirements?.length > 0 && order.requirements_complete && (
                    <span data-testid="requirements-complete" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-bold text-emerald-300">✓ All complete</span>
                  )}
                </div>
              </div>
              {order.requirements?.length > 0 && (
                <ul className="mb-2 space-y-1.5">
                  {order.requirements.map((r, i) => {
                    const done = !!(order.requirements_done || [])[i];
                    const rush = isRushText(r);
                    return (
                      <li key={i} data-testid={`requirement-row-${i}`}
                        className={`flex items-start gap-2 rounded-md px-2 py-1 text-sm ${rush && !done ? "border border-red-500/40 bg-red-500/10" : ""}`}>
                        <input type="checkbox" data-testid={`requirement-${i}`} checked={done}
                          onChange={() => toggleRequirement(i)} className="mt-0.5 h-4 w-4 accent-teal shrink-0" />
                        <span className={`${done ? "text-slate-500 line-through" : rush ? "text-red-300 font-semibold" : "text-slate-700"}`}>
                          {rush && !done && (
                            <span data-testid={`rush-badge-${i}`} className="mr-1.5 inline-flex items-center rounded-full bg-red-500/25 px-1.5 py-0.5 text-[9px] font-bold text-red-200 align-middle">RUSH</span>
                          )}
                          {r}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {order.instructions && <p className="text-sm text-slate-500 whitespace-pre-wrap">{order.instructions}</p>}
            </div>
          )}

          {/* Abstract Notes (manual template — visible & editable by all users) */}
          <div data-testid="abstract-notes-card" className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="flex items-center gap-2 text-sm font-700 text-slate-800">
                <ClipboardList className="h-4 w-4 text-teal" /> Abstract Notes
              </h3>
              <button data-testid="save-notes-btn" onClick={saveNotes} disabled={savingNotes || notesDraft === (order.notes || "")}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-50">
                {savingNotes ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Save
              </button>
            </div>
            <textarea data-testid="abstract-notes-text" value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)}
              disabled={locked} rows={16} spellCheck={false}
              className="w-full rounded-md border border-slate-300 bg-slate-50 p-3 text-xs font-mono leading-relaxed text-slate-800 focus:ring-2 focus:ring-teal focus:outline-none disabled:bg-slate-100 whitespace-pre" />
          </div>
        </div>

        <div className="col-span-12 lg:col-span-6 space-y-6">
          {/* Search Note */}
          <div data-testid="search-note-card" className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-2 text-sm font-700 text-slate-800"><FileText className="h-4 w-4 text-teal" /> Search Note</h3>
              <button data-testid="open-search-note" onClick={() => setSearchNoteOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-2 transition-colors">
                <ClipboardList className="h-3.5 w-3.5" /> {order.search_note ? "Edit / Copy" : "Open Search Note"}
              </button>
            </div>
            {(snData || order.search_note) ? (
              (() => { const s = snData || order.search_note; return (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {[["Type", s.type_of_search], ["Effective", s.effective_date],
                  ["Property ID", s.property_id], ["Total Value", s.total_value],
                  ["Short Legal", s.short_legal]].map(([k, v]) => (v ? (
                  <div key={k} className={k === "Short Legal" ? "col-span-2" : ""}>
                    <dt className="text-[11px] uppercase tracking-wide text-slate-500">{k}</dt>
                    <dd className="text-slate-700 line-clamp-2">{v}</dd>
                  </div>
                ) : null))}
                <div className="col-span-2 text-[11px] text-teal">✦ AI pre-filled — click Edit / Copy to review</div>
              </dl>
              ); })()
            ) : (
              <p className="text-sm text-slate-500">Building the Abstract Notes from the client's documents…</p>
            )}
          </div>

          {/* Typing Report pointer */}
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-700 text-slate-800"><FileText className="h-4 w-4 text-teal" /> Typing Report</h3>
              <a data-testid="goto-report-builder" href="#report-builder" className="text-xs font-semibold text-teal hover:underline">Go to builder ↓</a>
            </div>
            <p className="mt-2 text-sm text-slate-500">Fill and finalize the {order.report_status === "Final" ? "finalized " : ""}report below, then export to Word / branded PDF.</p>
          </div>
        </div>
      </div>

      {/* Clarifications: client instructions, completion documents, chat + call */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-6 rounded-lg border border-slate-200 bg-white p-5" data-testid="instructions-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-slate-500" /><h3 className="font-display text-lg font-600">Client Instructions</h3></div>
            {isAdmin && instrConfig && (
              <select data-testid="instruction-set-select" value={order.instruction_set || ""} onChange={(e) => changeInstructionSet(e.target.value)} className="rounded-md border border-slate-300 px-2 py-1 text-xs focus:ring-2 focus:ring-teal focus:outline-none">
                <option value="">Set…</option>
                {Object.keys(instrConfig).map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            )}
          </div>
          {order.instruction_set && instrConfig?.[order.instruction_set] ? (
            <>
              <div className="text-xs font-semibold text-slate-500 mb-2">{instrConfig[order.instruction_set].title}</div>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                {instrConfig[order.instruction_set].items.map((it, i) => <li key={i} data-testid={`instr-item-${i}`}>{it}</li>)}
              </ul>
            </>
          ) : (
            <p className="text-sm text-slate-500">No instruction set assigned yet.</p>
          )}
          {order.instructions && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-500 mb-1">Order Sheet Notes</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.instructions}</p>
            </div>
          )}
          {(order.requirements || []).length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-semibold text-slate-500 mb-1">Captured Requirements</div>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                {order.requirements.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-6 rounded-lg border border-slate-200 bg-white p-5" data-testid="documents-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-500" /><h3 className="font-display text-lg font-600">Completion Documents</h3></div>
            <div>
              <input ref={docInput} type="file" className="hidden" data-testid="doc-file-input" onChange={(e) => uploadDoc(e.target.files[0])} />
              <button data-testid="upload-doc-btn" onClick={() => docInput.current?.click()} disabled={uploadingDoc} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-50">
                {uploadingDoc ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />} Upload
              </button>
            </div>
          </div>
          {(order.documents || []).length === 0 ? (
            <p className="text-sm text-slate-500">No documents uploaded yet. Upload finished reports or packages here.</p>
          ) : (
            <div className="space-y-2">
              {order.documents.map((d, i) => (
                <a key={i} data-testid={`doc-${i}`} href={fileUrl(d.url)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 hover:border-primary transition-colors">
                  <span className="flex items-center gap-2 text-sm text-slate-700 min-w-0"><FileText className="h-4 w-4 text-slate-500 shrink-0" /><span className="truncate">{d.name}</span></span>
                  <span className="flex items-center gap-2 text-xs text-slate-500 shrink-0">{humanSize(d.size)} <Download className="h-3.5 w-3.5" /></span>
                </a>
              ))}
            </div>
          )}
        </div>

        {isAdmin && order.email_meta && (order.email_meta.subject || order.email_meta.from || order.email_meta.date) && (
        <div className="col-span-12 rounded-lg border border-slate-200 bg-white p-5" data-testid="source-email-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-500" /><h3 className="font-display text-lg font-600">Source Email</h3></div>
            {order.email_meta.eml_url && (
              <a data-testid="download-eml-btn" href={fileUrl(order.email_meta.eml_url)} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                <Download className="h-3.5 w-3.5" /> Open original (.eml)
              </a>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div><div className="text-[11px] uppercase font-semibold text-slate-400">From</div><div data-testid="source-email-from" className="text-slate-800 truncate" title={order.email_meta.from}>{order.email_meta.from || "—"}</div></div>
            <div><div className="text-[11px] uppercase font-semibold text-slate-400">Received</div><div data-testid="source-email-date" className="text-slate-800">{order.email_meta.date ? new Date(order.email_meta.date).toLocaleString() : "—"}</div></div>
            <div><div className="text-[11px] uppercase font-semibold text-slate-400">Order Date</div><div className="text-slate-800">{order.order_date ? new Date(order.order_date).toLocaleString() : "—"}</div></div>
          </div>
          <div className="mt-2"><div className="text-[11px] uppercase font-semibold text-slate-400">Subject</div><div data-testid="source-email-subject" className="text-slate-800">{order.email_meta.subject || "—"}</div></div>
          <p className="mt-2 text-[11px] text-slate-400">Linked to the original client email{(order.client_attachments || []).length ? " and order sheet below" : ""}.</p>
        </div>
        )}

        {isAdmin && (
        <div className="col-span-12 rounded-lg border border-slate-200 bg-white p-5" data-testid="client-docs-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-slate-500" /><h3 className="font-display text-lg font-600">Client Order Documents</h3></div>
            {isAdmin && (
              <div>
                <input ref={caInput} type="file" className="hidden" data-testid="client-doc-input" onChange={(e) => uploadClientDoc(e.target.files[0])} />
                <button data-testid="upload-client-doc-btn" onClick={() => caInput.current?.click()} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                  <UploadCloud className="h-3.5 w-3.5" /> Attach order sheet / deed
                </button>
              </div>
            )}
          </div>
          {(order.client_attachments || []).length === 0 ? (
            <p className="text-sm text-slate-500">The original client order sheet and deed pages will appear here for the assignee.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {order.client_attachments.map((d, i) => (
                <a key={i} data-testid={`client-doc-${i}`} href={fileUrl(d.url)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 hover:border-primary transition-colors">
                  <span className="flex items-center gap-2 text-sm text-slate-700 min-w-0"><FileText className="h-4 w-4 text-slate-500 shrink-0" /><span className="truncate">{d.name}</span></span>
                  <Download className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>
        )}

        <div className="col-span-12 rounded-lg border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-slate-500" /><h3 className="font-display text-lg font-600">Clarifications</h3></div>
            <button data-testid="start-call-btn" onClick={() => setShowCall((s) => !s)} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
              <Video className="h-3.5 w-3.5" /> {showCall ? "Hide call" : "Start call / screen share"}
            </button>
          </div>
          {showCall && <div className="mb-4"><JitsiRoom room={`TitleHubOps-Order-${order.id}`} height="55vh" /></div>}
          <ChatPanel scope="order" orderId={order.id} height="h-[360px]" />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left column */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {/* Dynamic workspace */}
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-600 text-slate-800">{order.product_type} Workspace</h3>
              <span className="text-xs text-slate-500">Fields tailored to this search type</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {wf.fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea data-testid={`ws-${f.key}`} rows={3} value={ws[f.key] || ""} onChange={(e) => setWs({ ...ws, [f.key]: e.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" />
                  ) : f.type === "date" ? (
                    <DatePicker testid={`ws-${f.key}`} value={ws[f.key] || ""} onChange={(v) => setWs({ ...ws, [f.key]: v })} />
                  ) : (
                    <input data-testid={`ws-${f.key}`} type={f.type} value={ws[f.key] || ""} onChange={(e) => setWs({ ...ws, [f.key]: e.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" />
                  )}
                </div>
              ))}
            </div>
            <button data-testid="save-workspace-btn" onClick={saveWorkspace} disabled={saving}
              className="mt-5 inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors duration-200 disabled:opacity-50">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Workspace"}
            </button>
          </div>

          {/* Automated Typing & Legal/Vesting Report */}
          <div id="report-builder" className="rounded-lg border border-slate-200 bg-white p-5 scroll-mt-24">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                <h3 className="font-display text-lg font-600 text-slate-800">Report Builder</h3>
                {locked && (
                  <span data-testid="report-final-badge" className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-300">
                    <Lock className="h-3 w-3" /> FINAL
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select data-testid="report-type-select" value={reportType} onChange={(e) => setReportType(e.target.value)} disabled={locked}
                  className="rounded-md border border-slate-300 px-2 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-teal focus:outline-none disabled:bg-slate-100">
                  {Object.keys(templates).map((t) => <option key={t} value={t}>{`${t} Report`}</option>)}
                </select>
              </div>
            </div>
            {locked ? (
              <div className="mb-4 space-y-2">
                <div data-testid="report-final-stamp" className="flex items-center justify-between gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/15 px-3 py-2">
                  <p className="text-xs text-emerald-300 flex items-center gap-1.5">
                    <Lock className="h-3.5 w-3.5" /> Finalized by <b>{order.report_finalized_by}</b> on {order.report_finalized_at ? fmt(order.report_finalized_at) : ""} — locked from edits.
                  </p>
                  <button data-testid="reopen-report-btn" onClick={reopenReport}
                    className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20 transition-colors">
                    Reopen to edit
                  </button>
                </div>
                {order.final_report?.url && (
                  <a data-testid="final-report-pdf-link" href={fileUrl(order.final_report.url)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-md border border-primary/30 bg-accent px-3 py-1.5 text-xs font-semibold text-teal hover:bg-primary hover:text-white transition-colors">
                    <FileText className="h-3.5 w-3.5" /> Client-ready PDF attached — {order.final_report.name}
                  </a>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-500 mb-4 flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> Type the {reportType} report details below. Review, then finalize &amp; export with your company logo.
              </p>
            )}

            {/* Report Export — manual-entry tracker (AI auto-fill removed) */}
            {!locked && (
              <div data-testid="report-export-panel" className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <h4 className="text-sm font-700 text-slate-800">Report Export</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Fill the fields below, then export the branded <b>{order.company ? (String(order.company).toLowerCase().includes("ydeal") ? "YDeal" : "TitlePriority") : "company"}</b> Word or PDF.</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <button data-testid="report-download-docx" onClick={downloadReportDocx}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
                      <Download className="h-3.5 w-3.5" /> Word
                    </button>
                    <button data-testid="report-download-pdf" onClick={downloadReportPdf}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
                      <Download className="h-3.5 w-3.5" /> PDF
                    </button>
                  </div>
                </div>
              </div>
            )}

            {(templates[reportType] || []).map((sec) => (
              <div key={sec.title} className="mb-5">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 border-b border-slate-200 pb-1">{sec.title}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sec.fields.map((f) => {
                    const big = f.type === "textarea" || f.type === "legal";
                    const tint = aiTinted(f.key) ? " ring-1 ring-amber-300 bg-amber-50" : "";
                    return (
                      <div key={f.key} className={big ? "md:col-span-2" : ""}>
                        <label className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                          {f.label}
                          {aiFilled.has(f.key) && !locked && (
                            verifiedSet.has(f.key) ? (
                              <button type="button" data-testid={`verified-${f.key}`} onClick={() => toggleVerified(f.key)}
                                title="Verified — click to unmark" className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0 text-[9px] font-bold text-emerald-700 hover:bg-emerald-200 transition-colors">
                                <CheckCircle2 className="h-2.5 w-2.5" /> Verified
                              </button>
                            ) : (
                              <button type="button" data-testid={`ai-filled-${f.key}`} onClick={() => toggleVerified(f.key)}
                                title="Auto-filled by AI — click to mark verified" className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0 text-[9px] font-bold text-amber-700 hover:bg-amber-200 transition-colors">
                                <Sparkles className="h-2.5 w-2.5" /> AI
                              </button>
                            )
                          )}
                        </label>
                        {big ? (
                          <textarea data-testid={`report-${f.key}`} rows={f.type === "legal" ? 6 : 3} value={report[f.key] || ""} disabled={locked}
                            onChange={(e) => setReport({ ...report, [f.key]: e.target.value })}
                            className={`w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-teal focus:outline-none disabled:bg-slate-100 disabled:text-slate-500${tint}`} />
                        ) : f.type === "date" ? (
                          <DatePicker testid={`report-${f.key}`} value={report[f.key] || ""} disabled={locked} onChange={(v) => setReport({ ...report, [f.key]: v })} />
                        ) : (
                          <input data-testid={`report-${f.key}`} value={report[f.key] || ""} disabled={locked}
                            onChange={(e) => setReport({ ...report, [f.key]: e.target.value })}
                            className={`w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none disabled:bg-slate-100 disabled:text-slate-500${tint}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Editable extra recorded instruments (arrays captured by AI) */}
            {reportType === "Typing" && !locked && (
              <div className="mb-5 space-y-4">
                {aiFilled.size > 0 && (
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="flex items-center gap-1.5 text-[11px] text-amber-700"><Sparkles className="h-3 w-3" /> Fields tinted amber were auto-filled by AI — click each <b>AI</b> badge to mark it verified (turns green). {[...aiFilled].filter((k) => !verifiedSet.has(k)).length} left to verify.</p>
                    <button type="button" data-testid="mark-all-verified-btn"
                      onClick={() => setReport({ ...report, verified_fields: [...aiFilled] })}
                      className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Mark all verified
                    </button>
                  </div>
                )}
                <EntryRows title="Additional Deeds (Chain of Title)" arrKey="deeds" startIndex={1}
                  entries={report.deeds || []} onChange={(a) => setArray("deeds", a)}
                  fields={[["deed_type", "Deed Type"], ["grantor", "Grantor"], ["grantee", "Grantee"], ["consideration", "Consideration"], ["dated_date", "Dated"], ["rec_date", "Rec Date"], ["book_page", "Book/Page"]]} />
                <EntryRows title="Additional Mortgages" arrKey="mortgages" startIndex={1}
                  entries={report.mortgages || []} onChange={(a) => setArray("mortgages", a)}
                  fields={[["lender", "Lender"], ["borrower", "Borrower"], ["instrument", "Instrument"], ["amount", "Amount"], ["dated_date", "Dated"], ["rec_date", "Rec Date"], ["book_page", "Book/Page"], ["maturity", "Maturity"], ["chain_status", "Chain Status (auto)"]]} />
                <EntryRows title="Additional Assignments" arrKey="assignments" startIndex={1}
                  entries={report.assignments || []} onChange={(a) => setArray("assignments", a)}
                  fields={[["assignor", "Assignor"], ["assignee", "Assignee"], ["dated_date", "Dated"], ["rec_date", "Rec Date"], ["book_page", "Book/Page"]]} />
                <EntryRows title="Additional Tax Years / Installments" arrKey="taxes" startIndex={1}
                  entries={report.taxes || []} onChange={(a) => setArray("taxes", a)}
                  fields={[["year", "Tax Year"], ["status", "Status"], ["installment_1_amount", "1st Amount"], ["installment_1_due", "1st Due"], ["installment_2_amount", "2nd Amount"], ["installment_2_due", "2nd Due"], ["parcel", "Parcel"], ["comments", "Comments"]]} />
                <EntryRows title="Judgments & Liens" arrKey="judgments" startIndex={0}
                  entries={report.judgments || []} onChange={(a) => setArray("judgments", a)}
                  fields={[["type", "Type"], ["creditor", "Creditor / Plaintiff"], ["debtor", "Debtor / Defendant"], ["amount", "Amount"], ["case_number", "Case No."], ["rec_date", "Rec Date"], ["book_page", "Book/Page"], ["status", "Status"]]} />
                <EntryRows title="Bankruptcies" arrKey="bankruptcies" startIndex={0}
                  entries={report.bankruptcies || []} onChange={(a) => setArray("bankruptcies", a)}
                  fields={[["debtor", "Debtor"], ["chapter", "Chapter"], ["case_number", "Case No."], ["court", "Court"], ["filed_date", "Filed Date"], ["discharge_date", "Discharge Date"], ["status", "Status"]]} />
                <EntryRows title="UCC Financing Statements" arrKey="uccs" startIndex={0}
                  entries={report.uccs || []} onChange={(a) => setArray("uccs", a)}
                  fields={[["secured_party", "Secured Party"], ["debtor", "Debtor"], ["filing_number", "Filing No."], ["filed_date", "Filed Date"], ["book_page", "Book/Page"], ["status", "Status"]]} />
                <EntryRows title="Lis Pendens / Pending Suits" arrKey="lis_pendens" startIndex={0}
                  entries={report.lis_pendens || []} onChange={(a) => setArray("lis_pendens", a)}
                  fields={[["plaintiff", "Plaintiff"], ["defendant", "Defendant"], ["case_number", "Case No."], ["court", "Court"], ["rec_date", "Rec Date"], ["book_page", "Book/Page"], ["status", "Status"]]} />
                <EntryRows title="Easements / Rights-of-Way" arrKey="easements" startIndex={0}
                  entries={report.easements || []} onChange={(a) => setArray("easements", a)}
                  fields={[["type", "Type"], ["in_favor_of", "In Favor Of"], ["purpose", "Purpose"], ["rec_date", "Rec Date"], ["book_page", "Book/Page"], ["comments", "Comments"]]} />
                <EntryRows title="Restrictive Covenants / CC&Rs" arrKey="ccrs" startIndex={0}
                  entries={report.ccrs || []} onChange={(a) => setArray("ccrs", a)}
                  fields={[["type", "Type"], ["declarant", "Declarant / Association"], ["rec_date", "Rec Date"], ["book_page", "Book/Page"], ["comments", "Comments"]]} />
                <EntryRows title="Satisfactions / Discharges / Releases" arrKey="satisfactions" startIndex={0}
                  entries={report.satisfactions || []} onChange={(a) => setArray("satisfactions", a)}
                  fields={[["instrument", "Instrument (Satisfaction/Discharge/Release)"], ["lender", "Lender / Mortgagee"], ["of_book_page", "Releases Mortgage Book/Page"], ["rec_date", "Rec Date"], ["book_page", "Book/Page"], ["status", "Status"]]} />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-2">
              <button data-testid="save-report-btn" onClick={saveReport} disabled={savingReport || locked}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-navy-2 transition-colors duration-200 disabled:opacity-50">
                <Save className="h-4 w-4" /> {savingReport ? "Saving…" : "Save Report"}
              </button>
              {!locked && (
                <button data-testid="finalize-report-btn" onClick={() => setConfirmFinalize(true)}
                  className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors duration-200">
                  <Lock className="h-4 w-4" /> Finalize Report
                </button>
              )}
              <button data-testid="preview-report-btn" onClick={openPreview}
                className="inline-flex items-center gap-2 rounded-md bg-[#1A395E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#24497A] transition-colors duration-200">
                <FileText className="h-4 w-4" /> Preview Report
              </button>
              <button data-testid="download-report-docx" onClick={downloadReportDocx}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors duration-200">
                <Download className="h-4 w-4" /> Download Word (.docx)
              </button>
              <button data-testid="download-report-pdf" onClick={downloadReportPdf}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors duration-200">
                <Download className="h-4 w-4" /> Download PDF
              </button>
              <div className="w-full flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-slate-200">
                <span className="text-xs font-semibold text-slate-500">Blank Templates ({order.company || "auto"}):</span>
                <button data-testid="download-blank-lnv" onClick={() => downloadBlankTemplate("lnv")}
                  className="inline-flex items-center gap-2 rounded-md border border-teal/40 bg-teal/10 px-3 py-1.5 text-xs font-semibold text-teal hover:bg-teal/20 transition-colors">
                  <FileText className="h-3.5 w-3.5" /> LNV (Search) Template
                </button>
                <button data-testid="download-blank-typing" onClick={() => downloadBlankTemplate("typing")}
                  className="inline-flex items-center gap-2 rounded-md border border-teal/40 bg-teal/10 px-3 py-1.5 text-xs font-semibold text-teal hover:bg-teal/20 transition-colors">
                  <FileText className="h-3.5 w-3.5" /> Typing (Commitment) Template
                </button>
              </div>
            </div>
          </div>

          {/* Professional Deliverables */}
          <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="deliverables-card">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-lg font-600 text-slate-800">Professional Deliverables</h3>
              {order.report_data?.chain_polish && (
                <span data-testid="chain-summary-badge" className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-slate-600">
                  <ClipboardList className="h-3 w-3" /> {order.report_data.chain_polish.open || 0} open · {order.report_data.chain_polish.released || 0} released
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-3">Generate the branded <b>Name Search Index</b> &amp; <b>Search Note</b> PDFs from this order's data, and auto-flag open vs. released mortgages.</p>
            <div className="flex flex-wrap items-center gap-2">
              <button data-testid="polish-chain-btn" onClick={polishChain} disabled={polishing || locked}
                className="inline-flex items-center gap-2 rounded-md border border-teal/40 bg-teal/10 px-3 py-2 text-sm font-semibold text-teal hover:bg-teal/20 transition-colors disabled:opacity-50">
                {polishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Polish Title Chain
              </button>
              <button data-testid="generate-deliverables-btn" onClick={generateDeliverables} disabled={generatingDelv}
                className="inline-flex items-center gap-2 rounded-md bg-[#1A395E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#24497A] transition-colors disabled:opacity-50">
                {generatingDelv ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generate Deliverables
              </button>
              <button data-testid="download-packet-btn" onClick={downloadPacket}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors">
                <Download className="h-4 w-4" /> Download Hand-off Packet (.zip)
              </button>
              {isAdmin && (
                <button data-testid="email-packet-btn" onClick={emailPacket} disabled={emailingPacket}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
                  {emailingPacket ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email packet to client
                </button>
              )}
              <button data-testid="download-search-note-pdf-btn" onClick={() => downloadDeliverable("search_note")}
                className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
                <Download className="h-4 w-4" /> Search Note PDF
              </button>
            </div>
            {order.packet_email?.sent_at && (
              <div data-testid="packet-email-log" className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Packet emailed to {order.packet_email.to} on {fmt(order.packet_email.sent_at)}{order.packet_email.by ? ` · by ${order.packet_email.by}` : ""}
              </div>
            )}
            {(order.generated_docs || []).length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="generated-docs-list">
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Attached to this order</div>
                {(order.generated_docs || []).map((g, i) => (
                  <a key={i} data-testid={`generated-doc-${g.kind}`} href={fileUrl(g.url)} target="_blank" rel="noreferrer"
                    className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 hover:border-primary hover:bg-slate-50 transition-colors group">
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-700 group-hover:text-teal truncate">
                      <FileText className="h-3.5 w-3.5 text-teal shrink-0" /> {g.name}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-slate-500 shrink-0">{g.generated_at ? fmt(g.generated_at) : ""} <Download className="h-3.5 w-3.5" /></span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Digital Searcher (best-effort, any county) */}
          <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="digital-searcher-card">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-[#1A395E]" />
                <h3 className="font-display text-lg font-600 text-slate-800">Digital Searcher · {order.county ? `${order.county} County` : "County Portals"}</h3>
              </div>
              {ds?.status && (
                <span data-testid="ds-status-badge" className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                  ds.status === "running" ? "bg-amber-100 text-amber-700" : ds.status === "done" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                  {ds.status === "running" ? "Running…" : ds.status === "done" ? `Done · ${ds.captured || 0} captured` : "Error"}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mb-3">Best-effort automation that visits this county's public record portals, dismisses pop-ups, retries slow pages, and attaches a full-page capture of each to this order. Duval County uses precise handlers; other counties use the AI-researched official portals (verify captures). Some portals use bot-protection (CAPTCHA) and may need a manual lookup.</p>
            <button data-testid="run-digital-search-btn" onClick={runDigitalSearch} disabled={dsStarting || ds?.status === "running"}
              className="inline-flex items-center gap-2 rounded-md bg-[#1A395E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#24497A] transition-colors disabled:opacity-50">
              {(dsStarting || ds?.status === "running") ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {ds?.status === "running" ? "Searching portals…" : "Run Digital Search"}
            </button>
            {ds?.steps?.some((s) => ["blocked", "unreachable", "failed"].includes(s.status)) && ds?.status !== "running" && (
              <button data-testid="retry-blocked-btn" onClick={retryBlocked} disabled={dsStarting}
                className="ml-2 inline-flex items-center gap-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-50">
                <RefreshCw className="h-4 w-4" /> Retry blocked portals
              </button>
            )}
            {ds?.steps?.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="ds-steps">
                {ds.steps.map((s) => {
                  const cfg = {
                    pending: ["bg-slate-100 text-slate-500", "Queued"],
                    running: ["bg-amber-100 text-amber-700", "Running…"],
                    captured: ["bg-emerald-100 text-emerald-700", "Captured"],
                    blocked: ["bg-rose-100 text-rose-700", "Blocked (CAPTCHA)"],
                    unreachable: ["bg-rose-100 text-rose-700", "Unreachable"],
                    failed: ["bg-rose-100 text-rose-700", "Failed"],
                  }[s.status] || ["bg-slate-100 text-slate-500", s.status];
                  return (
                    <div key={s.key} data-testid={`ds-step-${s.key}`} className="flex items-start justify-between gap-2 rounded-md border border-slate-200 px-3 py-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700">{s.label}</span>
                          <span className={`rounded-full px-1.5 py-0 text-[9px] font-bold uppercase ${cfg[0]}`}>{cfg[1]}</span>
                        </div>
                        {s.note && <div className="text-[10px] text-slate-400 mt-0.5 truncate">{s.note}</div>}
                      </div>
                      {s.attachment?.url && (
                        <a data-testid={`ds-capture-${s.key}`} href={fileUrl(s.attachment.url)} target="_blank" rel="noreferrer"
                          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-teal hover:underline">
                          <ExternalLink className="h-3 w-3" /> View
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <h3 className="font-display text-lg font-600 text-slate-800 mb-4">Client Details</h3>
            {isAdmin ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="client-details-admin">
                <ClientRow label="Client Name" value={order.client_name} />
                <ClientRow label="Last Name" value={order.client_last_name} />
                <ClientRow label="Client Email" value={order.client_email} />
                <ClientRow label="Client Phone" value={order.client_phone} />
                <ClientRow label="SSN" value={order.client_ssn} />
                <div className="sm:col-span-2"><ClientRow label="Notes" value={order.client_notes} /></div>
              </div>
            ) : (
              <div data-testid="client-details-masked" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {["Client Name", "Client Email", "Client Phone", "SSN"].map((l) => (
                  <div key={l}>
                    <div className="text-xs font-semibold text-slate-700 mb-1">{l}</div>
                    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-100 px-3 py-2">
                      <Lock className="h-3.5 w-3.5 text-slate-500" />
                      <span className="font-mono text-sm text-slate-500 tracking-widest">••••••••</span>
                    </div>
                  </div>
                ))}
                <p className="sm:col-span-2 text-xs text-slate-500">Client contact &amp; sensitive data is restricted to administrators.</p>
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="col-span-12 lg:col-span-4 space-y-6 lg:sticky lg:top-24 lg:self-start">
          {/* Quick Research — universal (all roles, every order) */}
          <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="quick-research-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-teal" />
                <h3 className="font-display text-lg font-600 text-slate-800">Quick Research</h3>
              </div>
              <button data-testid="research-refresh" onClick={() => fetchResearch(true)} disabled={researchLoading}
                title="Re-fetch official county portals with AI"
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50">
                {researchLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Refresh
              </button>
            </div>
            {!order.state || !order.county ? (
              <p className="text-xs text-slate-500" data-testid="research-empty">Add a <b>state</b> &amp; <b>county</b> to this order to generate research links.</p>
            ) : (
              <div className="space-y-3" data-testid="research-links">
                <button data-testid="research-copy-all" onClick={copyAllResearch}
                  className={`w-full inline-flex items-center justify-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${copiedResearch ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-teal/40 bg-teal/10 text-teal hover:bg-teal/20"}`}>
                  {copiedResearch ? <><CheckCircle2 className="h-3.5 w-3.5" /> Copied ✓</> : <><Copy className="h-3.5 w-3.5" /> Copy all {researchItems.length} links</>}
                </button>
                {researchCategories.map((cat) => (
                  <div key={cat.title} data-testid={`research-cat-${cat.title}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">{cat.title}</div>
                    <div className="space-y-1.5">
                      {(cat.links || []).map((l0) => {
                        const l = researchItems.find((x) => x.key === l0.key) || l0;
                        return (
                          <a key={l.key} data-testid={`research-link-${l.key}`} href={l.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-start justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 hover:border-primary hover:bg-slate-50 transition-colors group">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-700 group-hover:text-teal truncate">{l.label}</div>
                              {l.ai && !research?.verified && <div className="text-[10px] text-slate-400 mt-0.5">{l.fallback ? "AI unsure — opens a web search to verify" : "AI-sourced · verify before relying"}</div>}
                            </div>
                            <ExternalLink className="h-3.5 w-3.5 text-slate-700 group-hover:text-teal mt-0.5 shrink-0" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {researchLoading && !research && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding county portals…</div>}
                {research?.verified ? (
                  <div data-testid="research-verified-badge" className="flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Links confirmed correct by the team{research.verified_by ? ` · ${research.verified_by}` : ""}
                  </div>
                ) : (
                  <button data-testid="research-confirm" onClick={confirmResearch} disabled={researchLoading}
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Confirm links correct for the team
                  </button>
                )}
                <button data-testid="report-bad-link" onClick={() => fetchResearch(true)} disabled={researchLoading}
                  className="w-full text-center text-[11px] text-slate-400 hover:text-rose-600 transition-colors disabled:opacity-50 pt-1">
                  <AlertTriangle className="inline h-3 w-3 mr-1 -mt-0.5" /> Report a bad link — re-fetch this county for the whole team
                </button>
              </div>
            )}
          </div>

          {/* Update status */}
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-600 text-slate-800">Update Status</h3>
              {isAdmin && <span data-testid="admin-override-badge" className="rounded-full bg-[#1A395E] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Admin · any stage</span>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {STATUSES.map((s) => (
                <button key={s} data-testid={`status-btn-${s.replace(/[^a-zA-Z]/g, "")}`} onClick={() => changeStatus(s)}
                  className={`rounded-md border px-2.5 py-2 text-xs font-semibold transition-colors duration-200 ${
                    order.status === s ? "border-[#1A395E] bg-[#1A395E] text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}>
                  {s}
                </button>
              ))}
            </div>
            <textarea data-testid="status-note" rows={2} placeholder="Optional note for this update…" value={note} onChange={(e) => setNote(e.target.value)}
              className="mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" />
          </div>

          {/* Timeline */}
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-slate-500" />
              <h3 className="font-display text-lg font-600 text-slate-800">Status Timeline</h3>
            </div>
            <ol className="relative border-l-2 border-slate-200 ml-2" data-testid="timeline">
              {[...historyWithDurations(order)].reverse().map((ev, i) => (
                <li key={i} className="mb-5 ml-4">
                  <span className={`absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ${ev.isCurrent ? "bg-[#00BFA6] ring-2 ring-[#00BFA6]/30" : "bg-primary"}`}>
                    <CheckCircle2 className="h-3 w-3 text-white" />
                  </span>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={ev.status} />
                    {ev.isCurrent && <span className="rounded-full bg-[#00BFA6]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#0f766e]">Current</span>}
                  </div>
                  <div className="mt-1 text-xs text-slate-500 font-mono">{fmt(ev.timestamp)}</div>
                  <div className="text-xs text-slate-500">by {ev.changed_by_name}</div>
                  <div data-testid={`stage-duration-${i}`} className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600">
                    <Clock className="h-3 w-3 text-slate-400" />
                    {ev.isCurrent ? `In this stage · ${humanizeDuration(ev.durationMs)}` : `Held · ${humanizeDuration(ev.durationMs)}`}
                  </div>
                  {ev.note && <div className="mt-1 text-sm text-slate-700">{ev.note}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmFinalize} onOpenChange={setConfirmFinalize}>
        <AlertDialogContent data-testid="finalize-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Finalize this report?</AlertDialogTitle>
            <AlertDialogDescription>
              The report will be locked from further edits. You can reopen it later if you need to make changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="finalize-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="finalize-confirm-btn" onClick={finalizeReport}
              className="bg-emerald-600 hover:bg-emerald-700">
              Finalize &amp; Lock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {searchNoteOpen && (
        <SearchNoteModal orderId={id} locked={locked} onClose={() => setSearchNoteOpen(false)}
          onSaved={(o) => { setOrder(o); if (o.search_note) setSnData(o.search_note); }} />
      )}

      {previewOpen && (
        <div data-testid="report-preview-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreviewOpen(false)}>
          <div className="relative max-h-[88vh] w-full max-w-3xl overflow-auto rounded-lg bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
              <span className="text-sm font-bold text-[#1A395E]">Report Preview {previewData ? `· ${previewData.report_type}` : ""}</span>
              <div className="flex items-center gap-2">
                <div className="flex rounded-md border border-slate-200 overflow-hidden text-xs font-semibold">
                  <button data-testid="preview-tab-pdf" onClick={() => setPreviewTab("pdf")} disabled={!previewPdfUrl}
                    className={`px-3 py-1 ${previewTab === "pdf" ? "bg-[#1A395E] text-white" : "text-slate-600 hover:bg-slate-50"} disabled:opacity-40`}>Exact layout</button>
                  <button data-testid="preview-tab-details" onClick={() => setPreviewTab("details")}
                    className={`px-3 py-1 ${previewTab === "details" ? "bg-[#1A395E] text-white" : "text-slate-600 hover:bg-slate-50"}`}>Details</button>
                </div>
                <button data-testid="report-preview-close" onClick={() => setPreviewOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="p-6">
              {previewLoading || !previewData ? (
                <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Building preview…</div>
              ) : previewTab === "pdf" && previewPdfUrl ? (
                <iframe data-testid="preview-pdf-frame" title="Report PDF" src={previewPdfUrl} className="w-full h-[70vh] rounded-md border border-slate-200" />
              ) : (
                <div className="mx-auto max-w-2xl">
                  <div className="text-center">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${previewData.logo_url}`} alt="logo" className="mx-auto h-14 object-contain" />
                    <div className="mt-2 text-lg font-bold text-slate-800">{previewData.company}</div>
                    <div className="text-sm font-semibold text-slate-500">{previewData.report_type} Report</div>
                  </div>
                  {previewData.sections.map((sec, si) => (
                    <div key={si} className="mt-5">
                      <div className="mb-2 border-b border-slate-200 pb-1 text-xs font-bold uppercase tracking-wide text-[#1A6FBF]">{sec.title}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                        {sec.rows.map((r, ri) => (
                          <div key={ri} className="flex justify-between gap-3 text-sm">
                            <span className="text-slate-500">{r.label}</span>
                            <span className={`font-medium text-right ${r.value ? "text-slate-800" : "text-slate-300 italic"}`}>{r.value || "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="mt-6 flex justify-center gap-2 border-t border-slate-200 pt-4">
                    <button onClick={downloadReportDocx} className="inline-flex items-center gap-2 rounded-md bg-[#1A395E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#24497A]"><Download className="h-4 w-4" /> Download Word</button>
                    <button onClick={downloadReportPdf} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> Download PDF</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientRow({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
      <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 font-mono">
        {value || <span className="text-slate-700">—</span>}
      </div>
    </div>
  );
}


function EntryRows({ title, arrKey, entries, fields, onChange, startIndex = 0 }) {
  const rows = entries.slice(startIndex);
  const blank = () => fields.reduce((a, [k]) => ({ ...a, [k]: "" }), {});
  const setCell = (i, key, val) => {
    const arr = [...entries];
    arr[startIndex + i] = { ...(arr[startIndex + i] || {}), [key]: val };
    onChange(arr);
  };
  const addRow = () => onChange([...entries, blank()]);
  const removeRow = (i) => { const arr = [...entries]; arr.splice(startIndex + i, 1); onChange(arr); };
  return (
    <div data-testid={`entry-rows-${arrKey}`} className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}{rows.length ? ` (${rows.length})` : ""}</div>
        <button type="button" data-testid={`add-${arrKey}`} onClick={addRow}
          className="inline-flex items-center gap-1 rounded-md border border-teal/40 bg-teal/10 px-2 py-1 text-[11px] font-semibold text-teal hover:bg-teal/20 transition-colors">
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="text-[11px] text-slate-400">None yet — click Add to enter one manually, or auto-fill from a package.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} data-testid={`row-${arrKey}-${i}`} className="rounded-md border border-amber-200 bg-amber-50/50 p-2">
              <div className="flex items-center justify-between mb-1">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700"><Sparkles className="h-2.5 w-2.5" /> #{startIndex + i + 1} · verify</span>
                <button type="button" data-testid={`remove-${arrKey}-${i}`} onClick={() => removeRow(i)}
                  className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-rose-600 hover:bg-rose-50 transition-colors">
                  <X className="h-2.5 w-2.5" /> Remove
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {fields.map(([key, label]) => (
                  <div key={key}>
                    <label className="text-[10px] font-semibold text-slate-500 mb-0.5 block">{label}</label>
                    <input data-testid={`cell-${arrKey}-${i}-${key}`} value={row[key] || ""}
                      onChange={(e) => setCell(i, key, e.target.value)}
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs focus:ring-1 focus:ring-teal focus:outline-none" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
